import React from 'react';
import { Box, Paper, Typography, Chip, Button } from '@mui/material';
import { School, PlayCircleFilled, CheckCircle } from '@mui/icons-material';
import { CourseSummaryDTO } from '../../services/studentDashboardService';

interface CourseCardProps {
  course: CourseSummaryDTO;
  continueLabel: string;
  onContinue: () => void;
}

/**
 * Course card with SVG circular progress ring, status chip, and a continue CTA.
 * The ring animates its fill on mount.
 */
const CourseCard: React.FC<CourseCardProps> = ({ course, continueLabel, onContinue }) => {
  const pct = course.progressPercent;
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference - (circumference * pct) / 100;

  const statusColor =
    course.statusLabel === 'Active' ? '#00b894' :
    course.statusLabel === 'Completed' ? '#0984e3' : '#b2bec3';

  const resumeColor =
    course.currentTopic?.resumeReason === 'IN_PROGRESS' ? '#00b894' :
    course.currentTopic?.resumeReason === 'RECOMMENDED' ? '#6c5ce7' : '#636e72';

  const resumeLabel =
    course.currentTopic?.resumeReason === 'IN_PROGRESS' ? 'In Progress' :
    course.currentTopic?.resumeReason === 'RECOMMENDED' ? 'Recommended' : 'Start Here';

  const ringColor = pct === 100 ? '#00b894' : pct > 50 ? '#6c5ce7' : '#00d2d3';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        border: '1px solid rgba(0,0,0,0.07)',
        height: '100%',
        transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
          borderColor: `${ringColor}55`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Chip
            label={course.statusLabel}
            size="small"
            sx={{
              bgcolor: `${statusColor}20`,
              color: statusColor,
              fontWeight: 700,
              mb: 1,
              borderRadius: 1.5,
              fontSize: '0.68rem',
              height: 22,
            }}
          />
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{ lineHeight: 1.3, fontSize: { xs: '1rem', sm: '1.1rem' } }}
          >
            {course.courseName}
          </Typography>
          {course.currentTopic ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip
                label={resumeLabel}
                size="small"
                sx={{
                  bgcolor: `${resumeColor}18`,
                  color: resumeColor,
                  fontWeight: 700,
                  borderRadius: 1,
                  fontSize: '0.62rem',
                  height: 20,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }} noWrap>
                {course.currentTopic.topicName}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
              No active topic
            </Typography>
          )}
        </Box>

        {/* Circular progress ring */}
        <Box sx={{ position: 'relative', width: 76, height: 76, flexShrink: 0 }}>
          <svg width="76" height="76" viewBox="0 0 76 76">
            <circle
              cx="38" cy="38" r={radius}
              fill="none"
              stroke="rgba(0,0,0,0.07)"
              strokeWidth="6"
            />
            <circle
              cx="38" cy="38" r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth="6"
              strokeDasharray={circumference}
              strokeDashoffset={strokeOffset}
              strokeLinecap="round"
              transform="rotate(-90 38 38)"
              style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)' }}
            />
          </svg>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {pct === 100 ? (
              <CheckCircle sx={{ color: '#00b894', fontSize: 22 }} />
            ) : (
              <>
                <Typography
                  variant="caption"
                  fontWeight="900"
                  sx={{ lineHeight: 1, fontSize: '0.88rem', color: ringColor }}
                >
                  {pct}%
                </Typography>
                <School sx={{ fontSize: 11, color: 'text.secondary', mt: 0.3 }} />
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Button
        fullWidth
        variant={pct === 0 ? 'contained' : 'outlined'}
        onClick={onContinue}
        startIcon={<PlayCircleFilled />}
        sx={{
          borderRadius: 2.5,
          py: 1.1,
          fontWeight: 700,
          textTransform: 'none',
          fontSize: '0.9rem',
          mt: 'auto',
          ...(pct === 0
            ? {}
            : { borderColor: `${ringColor}88`, color: ringColor, '&:hover': { bgcolor: `${ringColor}12` } }),
        }}
      >
        {pct === 0 ? 'Start Lesson' : pct === 100 ? 'Review Course' : continueLabel}
      </Button>
    </Paper>
  );
};

export default CourseCard;
